/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu/types';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import Heading from '@atlaskit/heading';
import { IconTile } from '@atlaskit/icon';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import CrossIcon from '@atlaskit/icon/core/cross';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { RovoIcon } from '@atlaskit/logo';
import { Flex, Inline, Text, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { Suggestion } from '../../../aiSuggestionsPluginType';

import { SuggestionCardFooter } from './SuggestionCardFooter';
import { SuggestionCardSources } from './SuggestionCardSources';

type SuggestionCardProps = {
	isHidingDeletedDiffs?: boolean;
	onAccept?: () => void;
	onBadSuggestion?: () => void;
	onClose?: () => void;
	onDiscard?: () => void;
	onDismiss?: () => void;
	onGoodSuggestion?: () => void;
	onNavigate?: (direction: 'up' | 'down') => void;
	onShowSidebar?: () => void;
	onToggleDeletedDiffs?: () => void;
	suggestion: Suggestion;
};

const styles = cssMap({
	sourceButton: {
		display: 'flex',
		backgroundColor: 'transparent',
		alignItems: 'center',
		paddingTop: token('space.100'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.0'),
		gap: token('space.100'),
		color: token('color.text.subtle'),
		font: token('font.body.small'),
	},
	card: {
		position: 'relative',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.xlarge'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		boxShadow: token('elevation.shadow.overlay'),
		width: '280px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		boxSizing: 'border-box',
	},
	rovoAuthorContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

export const SuggestionCard: React.ForwardRefExoticComponent<
	SuggestionCardProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, SuggestionCardProps>(function SuggestionCard(
	{
		onAccept,
		onDiscard,
		onDismiss,
		onGoodSuggestion,
		onBadSuggestion,
		onNavigate,
		onClose,
		onShowSidebar,
		onToggleDeletedDiffs,
		isHidingDeletedDiffs,
		suggestion,
	},
	ref,
): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isSourcesExpanded, setIsSourcesExpanded] = React.useState(false);

	const handleSourcesToggle = useCallback(() => {
		setIsSourcesExpanded((previous) => !previous);
	}, []);

	const renderMoreOptionsTrigger = useCallback(
		({
			triggerRef,
			'aria-controls': ariaControls,
			'aria-expanded': ariaExpanded,
			'aria-haspopup': ariaHasPopup,
			onClick,
			isSelected,
		}: CustomTriggerProps<HTMLButtonElement>) => (
			<IconButton
				ref={triggerRef}
				aria-controls={ariaControls}
				aria-expanded={ariaExpanded}
				aria-haspopup={ariaHasPopup}
				isSelected={isSelected}
				onClick={onClick}
				icon={ShowMoreHorizontalIcon}
				label={formatMessage(aiSuggestionsMessages.cardMoreOptionsButtonLabel)}
				appearance="subtle"
				spacing="default"
			/>
		),
		[formatMessage],
	);

	const ChevronUpSmall = useCallback(() => {
		return (
			<ChevronUpIcon
				label={formatMessage(aiSuggestionsMessages.cardExpandButtonLabel)}
				size="small"
			/>
		);
	}, [formatMessage]);

	const ChevronDownSmall = useCallback(() => {
		return (
			<ChevronDownIcon
				label={formatMessage(aiSuggestionsMessages.cardCollapseButtonLabel)}
				size="small"
			/>
		);
	}, [formatMessage]);

	const isInsight = suggestion.actionKind === 'INFORMATIONAL';

	return (
		<div ref={ref} css={[styles.card]}>
			<Flex alignItems="center" justifyContent="space-between">
				<Inline space="space.0" alignBlock="center">
					<IconButton
						icon={ChevronUpSmall}
						label={formatMessage(aiSuggestionsMessages.cardExpandButtonLabel)}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('up') : undefined}
					/>
					<IconButton
						icon={ChevronDownSmall}
						label={formatMessage(aiSuggestionsMessages.cardCollapseButtonLabel)}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('down') : undefined}
					/>
				</Inline>
				<Inline space="space.050" alignBlock="center">
					{onShowSidebar && (
						<IconButton
							icon={PanelRightIcon}
							label={formatMessage(aiSuggestionsMessages.cardShowSidebarButtonLabel)}
							appearance="subtle"
							spacing="compact"
							onClick={onShowSidebar}
						/>
					)}
					<IconButton
						icon={CrossIcon}
						label={formatMessage(aiSuggestionsMessages.cardCloseButtonLabel)}
						appearance="subtle"
						spacing="compact"
						onClick={onClose}
					/>
				</Inline>
			</Flex>

			<div css={styles.rovoAuthorContainer}>
				<RovoIcon
					label={formatMessage(aiSuggestionsMessages.cardRovoTitle)}
					size="small"
					shouldUseNewLogoDesign
					shouldUseHexLogo
				/>
				<Heading size="xsmall">{formatMessage(aiSuggestionsMessages.cardRovoTitle)}</Heading>
				<Tooltip
					position="top"
					content={formatMessage(aiSuggestionsMessages.cardPrivateTooltipLabelShort)}
					ignoreTooltipPointerEvents
				>
					<IconTile
						icon={LockLockedIcon}
						label={formatMessage(aiSuggestionsMessages.cardPrivateLabel)}
						appearance="gray"
						size="small"
					/>
				</Tooltip>
			</div>
			<Text as="p" color="color.text">
				{suggestion.description}
			</Text>
			{suggestion.reasoning && (
				<Flex alignItems="start" direction="column" gap="space.100">
					<Pressable xcss={styles.sourceButton} onClick={handleSourcesToggle}>
						{isSourcesExpanded
							? formatMessage(aiSuggestionsMessages.cardSourcesHideButtonLabel)
							: formatMessage(aiSuggestionsMessages.cardSourcesShowButtonLabel)}

						{isSourcesExpanded ? (
							<ChevronDownIcon label="" size="small" />
						) : (
							<ChevronRightIcon label="" size="small" />
						)}
					</Pressable>
					{isSourcesExpanded && (
						<Text as="p" color="color.text.subtle">
							{suggestion.reasoning}
						</Text>
					)}
					{!!suggestion?.sourceAris?.length && (
						<SuggestionCardSources sourceAris={suggestion.sourceAris} />
					)}
				</Flex>
			)}
			<Flex alignItems="center" justifyContent="space-between" gap="space.100">
				<Inline space="space.050" alignBlock="center">
					{isInsight ? (
						<Button appearance="subtle" onClick={onDismiss}>
							{formatMessage(aiSuggestionsMessages.cardDismissButtonLabel)}
						</Button>
					) : (
						<React.Fragment>
							<Button appearance="primary" onClick={onAccept}>
								{formatMessage(aiSuggestionsMessages.cardAcceptButtonLabel)}
							</Button>
							<Button appearance="subtle" onClick={onDiscard}>
								{formatMessage(aiSuggestionsMessages.cardDiscardButtonLabel)}
							</Button>
						</React.Fragment>
					)}
				</Inline>
				{!isInsight && (onToggleDeletedDiffs || onGoodSuggestion || onBadSuggestion) && (
					<DropdownMenu<HTMLButtonElement>
						placement="bottom-end"
						trigger={renderMoreOptionsTrigger}
					>
						<DropdownItemGroup>
							{onToggleDeletedDiffs && (
								<DropdownItem
									elemBefore={
										isHidingDeletedDiffs ? (
											<EyeOpenStrikethroughIcon label="" />
										) : (
											<EyeOpenIcon label="" />
										)
									}
									onClick={onToggleDeletedDiffs}
								>
									{isHidingDeletedDiffs
										? formatMessage(aiSuggestionsMessages.cardShowDeletedDiffsLabel)
										: formatMessage(aiSuggestionsMessages.cardHideDeletedDiffsLabel)}
								</DropdownItem>
							)}
							{onGoodSuggestion && (
								<DropdownItem elemBefore={<ThumbsUpIcon label="" />} onClick={onGoodSuggestion}>
									{formatMessage(aiSuggestionsMessages.cardGoodSuggestionLabel)}
								</DropdownItem>
							)}
							{onBadSuggestion && (
								<DropdownItem elemBefore={<ThumbsDownIcon label="" />} onClick={onBadSuggestion}>
									{formatMessage(aiSuggestionsMessages.cardBadSuggestionLabel)}
								</DropdownItem>
							)}
						</DropdownItemGroup>
					</DropdownMenu>
				)}
			</Flex>
			<SuggestionCardFooter />
		</div>
	);
});
